import { Button, Input, DateRangePicker } from "@/components";
import {
  DollarSign,
  TrendingUp,
  Calendar,
  Link2,
  Pencil,
  Target,
  Sparkles,
} from "lucide-react";
import React from "react";
import { useTranslation } from "@/contexts/LanguageContext";
import { useAds } from "../../hooks/AdsContext";
import apiService from "@/shared/apiService";
import { showToastError, showToastSuccess } from "@/shared/alertService";
import UtmParamsModal from "./UtmParamsModal";

const InvestmentConfig: React.FC = () => {
  const {
    platform,
    metaPlatform,
    dailyBudget,
    setDailyBudget,
    budgetMode,
    setBudgetMode,
    costPerResultGoal,
    setCostPerResultGoal,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    linkUrl,
    trackingParam,
    setTrackingParam,
    setIsTrackingParamManual,
  } = useAds();
  const { t } = useTranslation();
  const [isUtmModalOpen, setIsUtmModalOpen] = React.useState(false);
  const [suggestingGoal, setSuggestingGoal] = React.useState(false);

  const formatBRLInput = (value: number) =>
    value.toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const handleSuggestGoal = async () => {
    setSuggestingGoal(true);
    try {
      const data = await apiService.post("/api/agent/ads/suggest-cost-goal", {
        platform: metaPlatform || "facebook",
      });
      if (data.suggested_goal) {
        setCostPerResultGoal(formatBRLInput(data.suggested_goal));
        // Não sobrescreve um orçamento que o cliente já preencheu — só preenche se estiver vazio.
        if (data.suggested_daily_budget && !dailyBudget) {
          setDailyBudget(formatBRLInput(data.suggested_daily_budget));
        }
        if (data.basis === "benchmark_mercado") {
          showToastSuccess(
            data.reason ||
              t("Estimativa de mercado aplicada (ainda sem histórico real)."),
          );
        } else {
          showToastSuccess(data.reason || t("Meta sugerida com sucesso!"));
        }
      } else {
        showToastError(
          data.reason || t("Sem histórico suficiente para sugerir uma meta."),
        );
      }
    } catch (e: any) {
      showToastError(e.message || t("Erro ao sugerir meta de custo."));
    } finally {
      setSuggestingGoal(false);
    }
  };

  const budgetVal = Number((dailyBudget || "").replace(",", "."));
  const minHealthyBudget = platform === "chatgpt_ads" ? 25 : 20;
  const isBudgetHealthy = budgetVal >= minHealthyBudget;

  const periodDays = (() => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diff = end.getTime() - start.getTime();
    return Math.max(1, Math.ceil(diff / (1000 * 3600 * 24)));
  })();

  return (
    <div className="flex-1 flex flex-col bg-white rounded-2xl p-4 md:p-5 border border-purple-200/70 shadow-xs space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b pb-3 border-purple-100">
        <div className="flex items-start gap-2">
          <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-emerald-600 rounded-lg text-white shadow-xs">
            <DollarSign className="w-5 h-5" />
          </div>
          <div className="h-8 flex flex-col justify-between">
            <h2 className="text-xs font-bold text-gray-900 uppercase tracking-tight leading-none">
              {t("Orçamento & Período")}
            </h2>
            <span className="text-[10px] text-gray-400 font-medium leading-none">
              {t("Verba diária, datas e parâmetros de tracking")}
            </span>
          </div>
        </div>
        <div
          className={`flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold ${
            isBudgetHealthy
              ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
              : "bg-amber-50 text-amber-700 border border-amber-200"
          }`}
        >
          <div
            className={`w-1.5 h-1.5 rounded-full ${isBudgetHealthy ? "bg-emerald-500" : "bg-amber-500 animate-pulse"}`}
          />
          {isBudgetHealthy
            ? t("Verba Adequada")
            : platform === "chatgpt_ads"
              ? t("Mín. US$ 25/dia")
              : t("Mín. R$ 20/dia")}
        </div>
      </div>

      <div className="space-y-4">
        {/* Budget Input & Quick Presets */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label
              htmlFor="daily-budget-input"
              className="text-[10px] font-bold text-gray-700 uppercase tracking-tight"
            >
              {platform === "chatgpt_ads"
                ? t("Investimento Diário (USD)")
                : t("Investimento Diário (BRL)")}
              <span className="text-red-500 ml-1">*</span>
            </label>
            <div className="flex gap-1.5">
              {(platform === "chatgpt_ads"
                ? [25, 50, 100, 200]
                : [20, 50, 100, 200]
              ).map((val) => {
                const active = dailyBudget === String(val);
                return (
                  <button
                    key={val}
                    type="button"
                    onClick={() => setDailyBudget(String(val))}
                    className={`px-2.5 py-1 text-[10px] font-bold rounded-lg border transition-colors ${
                      active
                        ? "bg-purple-600 border-purple-600 text-white shadow-xs"
                        : "bg-white hover:bg-purple-100 text-purple-700 border-purple-200 shadow-xs"
                    }`}
                  >
                    {platform === "chatgpt_ads" ? "US$" : "R$"} {val}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="relative">
            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
              {platform === "chatgpt_ads" ? "US$" : "R$"}
            </span>
            <input
              id="daily-budget-input"
              type="text"
              value={dailyBudget}
              onChange={(e) => setDailyBudget(e.target.value)}
              placeholder="0,00"
              className="w-full h-10 pl-10 pr-9 text-sm font-bold rounded-xl border border-purple-200 bg-white focus:border-purple-600 focus:ring-2 focus:ring-purple-500/20 outline-hidden transition-colors"
              required
            />
            <TrendingUp className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400" />
          </div>
        </div>

        {/* Modo de Orçamento (ABO/CBO) + Meta de Custo por Resultado — só faz sentido pra Meta Ads */}
        {platform === "meta" && (
          <div className="space-y-3 pt-1">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-bold text-gray-700 uppercase tracking-tight">
                {t("Modo de Orçamento")}
              </label>
              <div className="flex bg-white rounded-lg p-0.5 border border-purple-200">
                <button
                  type="button"
                  onClick={() => setBudgetMode("ABO")}
                  className={`py-1.5 px-3 rounded-md text-xs font-bold transition-all ${
                    budgetMode === "ABO"
                      ? "bg-purple-600 text-white shadow-xs"
                      : "text-gray-500 hover:text-purple-700"
                  }`}
                >
                  {t("ABO (por conjunto)")}
                </button>
                <button
                  type="button"
                  onClick={() => setBudgetMode("CBO")}
                  className={`py-1.5 px-3 rounded-md text-xs font-bold transition-all ${
                    budgetMode === "CBO"
                      ? "bg-purple-600 text-white shadow-xs"
                      : "text-gray-500 hover:text-purple-700"
                  }`}
                >
                  {t("CBO (na campanha)")}
                </button>
              </div>
            </div>
            <p className="text-[10px] text-gray-400 -mt-1.5">
              {budgetMode === "ABO"
                ? t(
                    "Você controla quanto cada conjunto de anúncios gasta. Ideal para testar públicos.",
                  )
                : t(
                    "A Meta distribui a verba automaticamente entre os conjuntos. Ideal para escalar o que já funciona.",
                  )}
            </p>

            <div className="flex items-center justify-between">
              <label
                htmlFor="cost-per-result-input"
                className="text-[10px] font-bold text-gray-700 uppercase tracking-tight"
              >
                {t("Meta de Custo por Resultado (BRL)")}
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSuggestGoal}
                disabled={suggestingGoal}
                className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700 font-bold text-[10px] uppercase min-h-[28px]! h-7 px-2 rounded-lg shadow-none"
                icon={<Sparkles className="w-3 h-3" />}
              >
                {suggestingGoal ? t("Calculando...") : t("Sugerir meta")}
              </Button>
            </div>
            <div className="relative">
              <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-xs font-bold text-gray-400">
                R$
              </span>
              <input
                id="cost-per-result-input"
                type="text"
                value={costPerResultGoal}
                onChange={(e) => setCostPerResultGoal(e.target.value)}
                placeholder={t(
                  "Deixe em branco para a Meta otimizar por volume",
                )}
                className="w-full h-10 pl-10 pr-9 text-sm font-bold rounded-xl border border-purple-200 bg-white focus:border-purple-600 focus:ring-2 focus:ring-purple-500/20 outline-hidden transition-colors"
              />
              <Target className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-400" />
            </div>
            <p className="text-[10px] text-gray-400 -mt-1.5">
              {t(
                "A Meta tentará manter o custo médio por resultado próximo desse valor. Deixe em branco para priorizar volume.",
              )}
            </p>
          </div>
        )}

        {/* Schedule Section */}
        <div className="space-y-2.5 pt-1">
          <div className="flex items-center justify-between gap-1.5">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-700 uppercase tracking-wider">
              <Calendar className="w-3.5 h-3.5 text-purple-600" />
              <span>{t("Cronograma")}</span>
            </div>
            {periodDays > 0 && (
              <span className="text-[10px] font-bold text-purple-600">
                {t("{days} dias", { days: periodDays })}
              </span>
            )}
          </div>

          <DateRangePicker
            value={{ start: startDate, end: endDate }}
            onChange={(range) => {
              if (range.start) setStartDate(range.start);
              if (range.end) setEndDate(range.end);
            }}
            label={t("Período")}
            required
            className="w-full"
            variant="premium"
          />

          <div className="grid grid-cols-2 gap-2.5">
            <Input
              label={t("Horário Início")}
              hint={t("Janela diária — se repete todo dia do Período acima.")}
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              required
              inputSize="sm"
              className="text-xs rounded-xl border-purple-200"
            />
            <Input
              label={t("Horário Fim")}
              hint={t("Fora dessa janela, o anúncio não veicula naquele dia.")}
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              required
              inputSize="sm"
              className="text-xs rounded-xl border-purple-200"
            />
          </div>
        </div>

        {/* UTM Parameters */}
        <div className="pt-2 border-t border-purple-100/70 space-y-1.5">
          <div className="flex items-center justify-between gap-1.5">
            <div className="flex items-center gap-1.5 text-[10px] font-bold text-gray-700 uppercase tracking-wider">
              <Link2 className="w-3.5 h-3.5 text-purple-600" />
              <span>{t("Parâmetros UTM")}</span>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setIsUtmModalOpen(true)}
              className="bg-purple-50 hover:bg-purple-100 border-purple-200 text-purple-700 font-bold text-[10px] uppercase min-h-[32px]! h-8 px-2.5 rounded-lg shadow-none"
              icon={<Pencil className="w-3 h-3" />}
            >
              {t("Editar")}
            </Button>
          </div>
          <div className="p-2.5 bg-purple-50/20 border border-purple-100/70 rounded-xl text-[11px] font-mono text-gray-500 truncate">
            {trackingParam ||
              t("Nenhum parâmetro definido (padrão automático)")}
          </div>
        </div>
      </div>

      <UtmParamsModal
        open={isUtmModalOpen}
        onClose={() => setIsUtmModalOpen(false)}
        trackingParam={trackingParam}
        linkUrl={linkUrl}
        onSave={(value) => {
          setTrackingParam(value);
          setIsTrackingParamManual(true);
        }}
        onRestoreDefault={() => setIsTrackingParamManual(false)}
      />
    </div>
  );
};

export default InvestmentConfig;
